import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import { trackEvent } from '../../utils/analytics';

import './MarketingHeader.css';

/**
 * MarketingHeader — Apple/Linear-style top header for the logged-out marketing
 * surface. Logo left · nav center · Log in / Sign up right. Sticky, transparent
 * over the dark hero, hairline on scroll. Mobile → hamburger drawer.
 *
 * `slim` mode (auth pages /login + /signup): the center nav and the mobile
 * drawer are removed — PostHog showed visitors mid-signup clicking away
 * through those links and abandoning the form. The Log in / Sign up buttons
 * stay, and the logo remains a link home.
 *
 * Marketing brand routes use HYPHENATED slugs (/ai-employees, /super-agents,
 * /agent-to-agent) — distinct from the underscored APP routes (/ai_employee).
 *
 * Analytics (added Aug 20 2026 — the header previously fired NOTHING): every
 * header interaction is a `cta_click` with `page` = the path clicked FROM and
 * `location` = 'header' (desktop + slim) or 'header_drawer' (mobile drawer).
 * cta_names: nav_logo · nav_link (+ link=destination) · nav_login · nav_signup ·
 * nav_menu_open (burger open only). See TRACKING.md "marketing header nav".
 *
 * Book a demo left the header Nov 1 2026 (Nitish): the pill switched Home and
 * /ai-employees to a different header layout, so the header jumped when moving
 * to other pages, and it drew few clicks. It now sits beside Get started in the
 * closing section (AIEClose `demoPage`); every page renders the same header.
 */
const NAV = [
  { label: 'AI Employee', to: '/ai-employees' },
  { label: 'Super-Agents', to: '/super-agents' },
  { label: 'Agent-to-Agent', to: '/agent-to-agent' },
  // Tutorials -> Benchmarks Oct 17 2026 (Nitish, on Rohit's advice: the header link lends
  // /benchmarks authority); /tutorials stays reachable from the footer.
  { label: 'Benchmarks', to: '/benchmarks' },
  { label: 'Pricing', to: '/pricing' },
  // external: true -> plain <a> full page load. The blog is static HTML served
  // by nginx OUTSIDE the React router (see blog/README.md); a client-side
  // <Link> would dead-end in the SPA catch-all instead of reaching nginx.
  { label: 'Blog', to: '/blog/', external: true },
  // Contact removed from the header Oct 12 2026 (Nitish): the form drew mostly SEO/guest-post pitches;
  // /contact stays reachable from the footer, Pricing's Contact Sales and in-app support links.
];


// The logo's font, identical to the Helmet link on Home / AI Employees (so it is cached once, never fetched twice).
const HEADER_FONT_HREF = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Inter+Tight:wght@500;600;700;800&display=swap';

const MarketingHeader = ({ scrolled: scrolledProp, slim = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolledSelf, setScrolledSelf] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Primary path: MarketingLayout drives `scrolled` via a top-sentinel
  // IntersectionObserver (robust regardless of which element scrolls). When
  // the prop is provided we use it directly. As a fallback (e.g. if the header
  // is ever used without the layout), self-detect via scroll listeners.
  const usingProp = scrolledProp !== undefined;
  const scrolled = usingProp ? scrolledProp : scrolledSelf;

  // Safari (WebKit) flickers a backdrop-filter blur on a scrolling sticky header
  // — two structural CSS fixes (GPU layer, then a pseudo-element blur layer) did
  // not hold. So on Safari we drop the blur for a solid translucent veil: visually
  // near-identical on this near-black surface and physically unable to flicker
  // (no backdrop-filter to repaint). Chrome/Firefox keep the frosted blur. The UA
  // test excludes Chrome/Edge/Android and Chrome/Firefox-on-iOS, which all carry
  // "Safari" in their UA string.
  const isSafari =
    typeof navigator !== 'undefined' &&
    /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(navigator.userAgent);

  useEffect(() => {
    if (usingProp) return undefined;
    const contentEl = document.querySelector('.ant-layout-content.global-main-content');
    const onScroll = () => {
      const y = Math.max(
        window.scrollY || 0,
        document.documentElement.scrollTop || 0,
        contentEl ? contentEl.scrollTop : 0
      );
      setScrolledSelf(y > 12);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    if (contentEl) contentEl.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (contentEl) contentEl.removeEventListener('scroll', onScroll);
    };
  }, [usingProp]);

  // The logo is set in Inter Tight, which each page used to load on its own; Benchmarks, Pricing, Log in and Sign up
  // never did, so the logo fell back to Inter and its spacing shifted between pages (Nitish, Oct 29 2026). The header
  // loads the font itself, with the same URL the pages use, so the browser fetches it once.
  useEffect(() => {
    if (typeof document === 'undefined' || document.getElementById('mkt-header-font')) return;
    const link = document.createElement('link');
    link.id = 'mkt-header-font';
    link.rel = 'stylesheet';
    link.href = HEADER_FONT_HREF;
    document.head.appendChild(link);
  }, []);

  // Close the mobile menu on route change.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isActive = (to) => location.pathname === to;

  // Header analytics helper — `location` in the payload defaults to 'header';
  // drawer callers override it via `extra` (spread last so it wins).
  const track = (ctaName, extra = {}) =>
    trackEvent('cta_click', {
      cta_name: ctaName,
      page: location.pathname,
      location: 'header',
      ...extra,
    });

  // Slim (auth) header shows exactly ONE contextual cross-link on EVERY screen size
  // (Nitish, Oct 9 2026: desktop used to show both with "Sign up" always highlighted,
  // even on /signup): "Sign up" on /login, "Log in" everywhere else (/signup,
  // /forgot-password), always in the primary (highlighted) style. Nothing on the page
  // points at itself; the burger/menu never renders in slim mode.
  const slimShowsSignup = location.pathname === '/login';


  const loginButton = (
    <button
      type="button"
      className="mkt-header__login"
      onClick={() => {
        track('nav_login');
        navigate('/login');
      }}
    >
      Log in
    </button>
  );
  const signupButton = (
    <button
      type="button"
      className="mkt-header__signup"
      onClick={() => {
        track('nav_signup');
        navigate('/signup');
      }}
    >
      Sign up
    </button>
  );

  return (
    <header className={`mkt-header ${scrolled ? 'is-scrolled' : ''}${isSafari ? ' is-safari' : ''}${slim ? ' mkt-header--slim' : ''}`}>
      <div className="mkt-header__inner">
        <Link to="/" className="mkt-header__logo" onClick={() => track('nav_logo')}>
          CellCog
        </Link>

        {!slim && (
          <nav className="mkt-header__nav">
            {NAV.map((item) =>
              item.external ? (
                <a
                  key={item.to}
                  href={item.to}
                  className="mkt-header__link"
                  onClick={() => track('nav_link', { link: item.to })}
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`mkt-header__link ${isActive(item.to) ? 'is-active' : ''}`}
                  onClick={() => track('nav_link', { link: item.to })}
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>
        )}

        <div className="mkt-header__actions">
          {slim ? (
            <button
              type="button"
              className="mkt-header__signup mkt-header__slim-cta"
              onClick={() => {
                track(slimShowsSignup ? 'nav_signup' : 'nav_login');
                // CEL-3370: carry the pending return path (a Claude connect /authorize request, a cold deep link) across
                // the login <-> signup switch, like the in-form links do; without it the request was lost.
                navigate(slimShowsSignup ? '/signup' : '/login', { state: location.state });
              }}
            >
              {slimShowsSignup ? 'Sign up' : 'Log in'}
            </button>
          ) : (
            <>
              {loginButton}
              {signupButton}
            </>
          )}
          {!slim && (
            <button
              type="button"
              className="mkt-header__burger"
              aria-label="Menu"
              onClick={() => {
                if (!menuOpen) track('nav_menu_open');
                setMenuOpen((v) => !v);
              }}
            >
              {menuOpen ? <CloseOutlined /> : <MenuOutlined />}
            </button>
          )}
        </div>
      </div>

      {/* The NYT ribbon ran here Oct 14-20 2026; it is now a pill beside G2 in the fold
          (HomeFold.js, Nitish Oct 20 2026, CEL-3460). */}

      {/* Mobile drawer */}
      {!slim && (
        <div className={`mkt-header__drawer ${menuOpen ? 'is-open' : ''}`}>
          {NAV.map((item) =>
            item.external ? (
              <a
                key={item.to}
                href={item.to}
                className="mkt-header__drawer-link"
                onClick={() => track('nav_link', { link: item.to, location: 'header_drawer' })}
              >
                {item.label}
              </a>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                className="mkt-header__drawer-link"
                onClick={() => track('nav_link', { link: item.to, location: 'header_drawer' })}
              >
                {item.label}
              </Link>
            )
          )}
          <div className="mkt-header__drawer-actions">
            <button
              type="button"
              className="mkt-header__login"
              onClick={() => {
                track('nav_login', { location: 'header_drawer' });
                navigate('/login');
              }}
            >
              Log in
            </button>
            <button
              type="button"
              className="mkt-header__signup"
              onClick={() => {
                track('nav_signup', { location: 'header_drawer' });
                navigate('/signup');
              }}
            >
              Sign up
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default MarketingHeader;
